import { MouseEvent, useCallback } from 'react';
import { useToggle } from 'react-use';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { InventoryGlobalBulkActions } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions';
import { SetInventoryFlattenedViewSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryFlattenedViewSelectionCountLabel';
import { Content } from 'src/contexts/ContentContext';
import { NO_GROUP_ID } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Button, Stack, Switch } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutIcon } from 'src/svgs';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import {
  Feature,
  InventorySearchResult,
  UserSetting,
} from 'src/WebApiController';

import * as styles from './InventorySearchToolbar.css';

export const InventorySearchToolbar = ({
  disabled,
  setDisabled,
  activeSearchConfig,
  setLocalIsEventGroupingEnabled,
  localIsEventGroupingEnabled,
}: {
  disabled?: boolean;
  setDisabled?: (d: boolean) => void;
  activeSearchConfig?: InventorySearchResult;
  setLocalIsEventGroupingEnabled?: (enabled: boolean) => void;
  localIsEventGroupingEnabled?: boolean;
}) => {
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);

  const hasSearchViewGroupRowWithEventFeature = useUserHasFeature(
    Feature.SearchViewGroupRowWithEvent
  );

  const {
    value: storedInventoryFlattenedColumnsEnabledSetting,
    setUserSetting: setInventoryFlattenedColumnsEnabledSetting,
  } = useServerUserSetting<string[]>({
    id: UserSetting.InventoryFlattenedColumnsEnabled,
  });

  const {
    value: isEventGroupingEnabled,
    setUserSetting: setIsEventGroupingEnabled,
  } = useServerUserSetting<boolean>({
    id: UserSetting.GroupInventoryEventView,
  });

  const onColumnSettingButtonClickHandler = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!isColumnModalOpen) toggleColumnModal(true);
  };

  const handleToggleChange = useCallback(
    (isChecked: boolean) => {
      setLocalIsEventGroupingEnabled?.(isChecked);

      if (isChecked) {
        setInventoryFlattenedColumnsEnabledSetting(
          storedInventoryFlattenedColumnsEnabledSetting?.filter(
            (id) => id !== 'Event'
          ) || []
        );
      } else {
        setInventoryFlattenedColumnsEnabledSetting([
          'Event',
          ...(storedInventoryFlattenedColumnsEnabledSetting || []),
        ]);
      }

      setIsEventGroupingEnabled(isChecked);
    },
    [
      setInventoryFlattenedColumnsEnabledSetting,
      setIsEventGroupingEnabled,
      setLocalIsEventGroupingEnabled,
      storedInventoryFlattenedColumnsEnabledSetting,
    ]
  );

  return (
    <Stack
      gap="m"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.container}
    >
      <div style={{ flex: 1 }}>
        <SetInventoryFlattenedViewSelectionCountLabel>
          {!disabled && (
            <MultiSelectActionBar
              style={{ padding: 0 }}
              groupId={NO_GROUP_ID}
              displayMode="count-only"
              showHideOnSelection
            >
              <InventoryGlobalBulkActions />
            </MultiSelectActionBar>
          )}
        </SetInventoryFlattenedViewSelectionCountLabel>
      </div>

      <Stack gap="m">
        {hasSearchViewGroupRowWithEventFeature && (
          <Stack
            gap="m"
            alignItems="center"
            className={styles.groupEventsToggle}
          >
            <Content id={ContentId.GroupEvents} />
            <Switch
              checked={localIsEventGroupingEnabled}
              onCheckedChange={handleToggleChange}
            />
          </Stack>
        )}
        <Stack gap="m" alignItems="center" justifyContent="end">
          <Button
            variant="textPlain"
            style={{ paddingRight: vars.spacing.xxs }}
            onClick={onColumnSettingButtonClickHandler}
          >
            <LayoutIcon size={vars.iconSize.m} />
            <Content id={ContentId.Columns} />
          </Button>
        </Stack>
      </Stack>
      {isColumnModalOpen ? (
        <ColumnSettingsModal<CustomListingColumn>
          onClose={toggleColumnModal}
          sectionType={SectionType.ListingsFlattened}
          showTicketClassColor
        />
      ) : null}
    </Stack>
  );
};
